import {Component} from '@angular/core';
import {isNil} from 'lodash';
import {
  CustomerPortfolio,
  isCanceledPortfolio,
  isEnabledPortfolio,
  isInvestmentPortfolio,
  isPensionPlanPortfolio,
  isPremiumPortfolio
} from 'src/app/domain/model/customer-portfolio.model';
import {hasAccountBeenActivated, PortfolioAccount} from 'src/app/domain/model/portfolio/portfolio-account.model';
import CustomerPortfolioApi from 'src/app/domain/service/api/customer-portfolio.api';
import CustomerAreaRouteResolver from 'src/app/domain/service/route-resolver/customer-area-route-resolver.service';
import ProductRouteResolver from 'src/app/domain/service/route-resolver/product-route-resolver.service';
import {AppEnvironmentService} from 'src/app/infrastructure/environment/app-environment/app-environment.service';
import {WebComponent} from 'src/app/ui/webapp/component/web.component';
import MenuStatusStore from 'src/app/infrastructure/store/menuStatusStore';
import {Router} from 'src/app/infrastructure/router/router';
import UserApi from 'src/app/domain/service/api/user.api';
import CustomerApi from 'src/app/domain/service/api/customer/customer.api';
import PortfolioAccountApi from 'src/app/domain/service/api/portfolio-account.api';
import BackofficePortfolioApi from 'src/app/domain/service/api/backoffice-portfolio.api';
import {User} from 'src/app/domain/model/user.model';
import {Customer, hasTotalOperationalBlockReasons, isPhysicalCustomer, isPremiumCustomer} from 'src/app/domain/model/customer/customer.model';
import {BackofficePortfolio} from 'src/app/domain/model/backoffice-portfolio/backoffice-portfolio.model';
import {ThemeService} from 'src/app/domain/service/theme/theme.service';
import {PremiumModalService} from 'src/app/domain/service/premium-customer/premium-modal.service';
import ResumeSlideStore from 'src/app/infrastructure/store/resume-slide.store';

@Component({
  selector: 'app-customer-area-bottom-menu',
  templateUrl: 'bottom-menu.component.html',
  host: {
    class: 'lg:hidden',
  }
})
export class BottomMenuComponent extends WebComponent {
  public operationsOpen = false;
  public profileOpen = false;
  public isTransitionReady = false;
  public loggedUser: User | null = null;
  public customer: Customer | null = null;
  public account: PortfolioAccount | null = null;
  public selectedPortfolio: CustomerPortfolio | null = null;
  public selectedBoPortfolio: BackofficePortfolio | null = null;
  public newProductSlide = false;

  private accountSubscriptionId: string = null;
  private boPortfolioSubscriptionId: string = null;

  constructor(
    private readonly customerPortfolioApi: CustomerPortfolioApi,
    private readonly customerAreaRouteResolver: CustomerAreaRouteResolver,
    private readonly appEnvironment: AppEnvironmentService,
    private readonly menuStatusStore: MenuStatusStore,
    private readonly router: Router,
    private readonly userApi: UserApi,
    private readonly customerApi: CustomerApi,
    private readonly portfolioAccountApi: PortfolioAccountApi,
    private readonly backofficePortfolioApi: BackofficePortfolioApi,
    private readonly productRouteResolver: ProductRouteResolver,
    public readonly themeService: ThemeService,
    private readonly premiumModal: PremiumModalService,
    private readonly resumeSlideStore: ResumeSlideStore
  ) {
    super();
  }

  public isSelectedPortfolioEnabled(): boolean {
    return this.hasSelectedPortfolio() && isEnabledPortfolio(this.selectedPortfolio);
  }

  public isInvestment(): boolean {
    return this.hasSelectedPortfolio() && isInvestmentPortfolio(this.selectedPortfolio);
  }

  public hasSelectedPortfolio(): boolean {
    return this.selectedPortfolio !== null && this.selectedPortfolio !== undefined;
  }

  public canShowDeposit(): boolean {
    return this.hasSelectedPortfolio()
      && !isCanceledPortfolio(this.selectedPortfolio)
      && !isInvestmentPortfolio(this.selectedPortfolio);
  }

  public canShowExtraordinaryCashTransferIn(): boolean {
    return this.hasSelectedPortfolio()
      && !isCanceledPortfolio(this.selectedPortfolio)
      && isInvestmentPortfolio(this.selectedPortfolio)
      && hasAccountBeenActivated(this.account);
  }

  public canShowRegularCashTransferIn(): boolean {
    return this.hasSelectedPortfolio()
      && !isCanceledPortfolio(this.selectedPortfolio)
      && isInvestmentPortfolio(this.selectedPortfolio)
      && hasAccountBeenActivated(this.account);
  }

  public canShowWithdrawal(): boolean {
    return this.isSelectedPortfolioEnabled();
  }

  public canShowPortfolioConfiguration(): boolean {
    return this.hasSelectedPortfolio();
  }

  public canShowInvestmentTransferFundsButton(): boolean {
    return this.customer !== null
      && isPhysicalCustomer(this.customer)
      && hasAccountBeenActivated(this.account)
      && isInvestmentPortfolio(this.selectedPortfolio);
  }

  public canShowPensionPlanTransferButton(): boolean {
    return this.hasSelectedPortfolio()
      && isPensionPlanPortfolio(this.selectedPortfolio)
      && !isCanceledPortfolio(this.selectedPortfolio)
      && this.isSelectedPortfolioEnabled();
  }

  public shouldShowFeesMenuButton(): boolean {
    return this.isSelectedPortfolioEnabled();
  }

  public canShowPortfolioConfigurationButton(): boolean {
    return this.canShowPortfolioConfiguration() && !isCanceledPortfolio(this.selectedPortfolio);
  }

  public hasCustomerOperationalBlockReasons(): boolean {
    return hasTotalOperationalBlockReasons(this.customer);
  }

  public extraordinaryCashTransferInUrl(): string {
    return this.productRouteResolver.resolveAddMoneyUrl(this.selectedPortfolio);
  }

  public regularCashTransferInUrl(): string {
    return this.productRouteResolver.resolveAddMoneyUrl(this.selectedPortfolio, true);
  }

  public depositUrl(): string {
    return this.productRouteResolver.resolveAddMoneyUrl(this.selectedPortfolio);
  }

  public investmentTransferFundsUrl(): string {
    return this.productRouteResolver.resolveInvestmentTransferFunds();
  }

  public redirectRouteFeesPage(): string {
    return this.customerAreaRouteResolver.resolveFeePage();
  }

  public isResumeActive(): boolean {
    return this.router.currentUrl().url === '/client';
  }

  public isAllPortfoliosActive(): boolean {
    return this.router.currentUrl().url === this.allPortfoliosUrl();
  }

  public isMovementsActive(): boolean {
    return this.router.currentUrl().url === '/client/last-movements';
  }

  public isNewPlanActive(): boolean {
    return this.router.currentUrl().url.startsWith('/client/plan/create');
  }

  public isNewProductScreen(): boolean {
    const currentUrl = this.router.currentUrl().url;
    return this.isNewPlanActive()
      || (this.newProductSlide && currentUrl === this.customerAreaRouteResolver.resolveRoot());
  }

  public isPromotionsActive(): boolean {
    return this.router.currentUrl().url === '/client/invite/promotions';
  }

  public isInviteActive(): boolean {
    return this.router.currentUrl().url === '/client/invite/share';
  }

  public canShowPromotionsButton(): boolean {
    return this.canShowRecommendUsButton()
      && this.isInvestment()
      && !this.hasCustomerOperationalBlockReasons();
  }

  public isFeesActive(): boolean {
    return this.router.currentUrl().url === this.redirectRouteFeesPage();
  }

  public isProfileActive(): boolean {
    return this.router.currentUrl().url.startsWith('/client/profile');
  }

  public toggleOperations(): void {
    if (!this.operationsOpen) {
      this.closeProfile();
    }
    this.menuStatusStore.saveOperationsOpen(!this.operationsOpen);
  }

  public toggleProfile(): void {
    if (!this.profileOpen) {
      this.closeOperations();
    }
    this.menuStatusStore.saveProfileOpen(!this.profileOpen);
  }

  public closeOperations() {
    this.menuStatusStore.saveOperationsOpen(false);
  }

  public goToOperation(route: string) {
    this.closeOperations();
    this.router.navigate([route]);
  }

  public closeProfile() {
    this.menuStatusStore.saveProfileOpen(false);
  }

  public goToProfileOption(route: string) {
    this.closeProfile();
    this.router.navigate([route]);
  }

  public toggleTheme(event: MouseEvent): void {
    event.stopPropagation();
    this.themeService.toggleTheme();
  }

  public doPartnerProvidesUserData(): boolean {
    return AppEnvironmentService.doPartnerProvidesUserData();
  }

  public canShowPremiumButton(): boolean {
    return !isPremiumCustomer(this.customer)
      && this.isSelectedPortfolioEnabled()
      && !isPremiumPortfolio(this.selectedPortfolio)
      && isInvestmentPortfolio(this.selectedPortfolio);
  }

  public async showPremiumModal(): Promise<void> {
    await this.premiumModal.present();
  }

  public canShowRecommendUsButton(): boolean {
    return !this.appEnvironment.isAppForThirdPartners();
  }

  public isRecommendUsButtonOfTypeFriendPlan(): boolean {
    return isInvestmentPortfolio(this.selectedPortfolio);
  }

  public amountWithoutFees(): number {
    return this.appEnvironment.investmentPlanFreeAmountPerCustomerAdded();
  }

  public shouldShowButtonLogout(): boolean {
    return this.appEnvironment.shouldShowLogout();
  }

  public allPortfoliosUrl(): string {
    return this.customerAreaRouteResolver.resolveAllPortfolios();
  }

  protected onInit() {
    this.subscribe(this.menuStatusStore.operationsOpened$, (opened) => this.operationsOpen = opened);
    this.subscribe(this.menuStatusStore.profileOpened$, (opened) => this.profileOpen = opened);
    this.subscribe(this.resumeSlideStore.newProduct$, (newProduct) => this.newProductSlide = newProduct);
    this.isTransitionReady = true;

    this.subscribe(this.userApi.me(), (user) => {
      this.loggedUser = user;
      this.subscribe(this.customerApi.customer(), (customer) => this.customer = customer);
      this.subscribe(this.customerPortfolioApi.selected(), (portfolio) => {
        this.selectedPortfolio = portfolio;
        this.selectedBoPortfolio = null;

        if (!isNil(this.selectedPortfolio)) {
          this.unsubscribe(this.accountSubscriptionId);
          this.unsubscribe(this.boPortfolioSubscriptionId);

          this.accountSubscriptionId = this.subscribe(
            this.portfolioAccountApi.byCustomerPortfolioId(this.selectedPortfolio.id),
            (account) => this.account = account
          );

          this.boPortfolioSubscriptionId = this.subscribe(
            this.backofficePortfolioApi.byId(this.selectedPortfolio.portfolioMaster),
            (boPortfolio) => this.selectedBoPortfolio = boPortfolio
          );
        }
      });
    });
  }
}
